<template>
  <card-dialog
    v-model="modalVisible"
    max-width="800px"
    :title="$t('button.filters')"
  >
    <v-card-text class="pa-6">
      <v-row align="center">
        <v-col cols="4">
          {{ $t("picker.date.date_range.title") }}
        </v-col>
        <v-col cols="8">
          <v-menu
            v-model="filterDateAdvancedActive"
            :close-on-content-click="false"
          >
            <template v-slot:activator="{ on }">
              <v-text-field
                :value="filtersDirty.dateRange.length === 1 ? $dayjs(filtersDirty.dateRange[0]).format('ll - [today]') : (filtersDirty.dateRange.length === 2 ? filtersDirty.dateRange.map(d => $dayjs(d).format('ll')).join(' - ') : undefined)"
                class="custom-height-text-field"
                outlined
                clearable
                dense
                hide-details
                @click:clear="filtersDirty.dateRange = []"
                v-on="on"
              />
            </template>
            <v-date-picker
              v-model="filtersDirty.dateRange"
              :first-day-of-week="1"
              no-title
              range
              :min="filtersDirty.dateRange.length === 1 ? filtersDirty.dateRange[0] : undefined"
              :max="$dayjs().format('YYYY-MM-DD')"
              @change="$event.length === 2 ? filterDateAdvancedActive = false : undefined"
            />
          </v-menu>
        </v-col>
        <v-col cols="4">
          {{ $t("card_dialog.advanced_performance_filters.events") }}
        </v-col>
        <v-col cols="8">
          <v-autocomplete
            key="advancedSelectEvents"
            v-model="filtersDirty.eventIds"
            :items="events.data"
            :loading="events.loading"
            outlined
            dense
            hide-details
            item-value="id"
            item-text="title"
            :placeholder="$t('dropdown.all.placeholder')"
            multiple
            clearable
            :menu-props="{ maxWidth: '500px', width: '500px' }"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.title }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ $dayjs(item.start_at).format('dddd, ll HH:mm:ss') }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
            <template v-slot:selection="{ item, index }">
              <span
                v-if="index > 0 && index < 2"
                class="mr-1"
              >, </span>
              <span v-if="index >= 0 && index < 2">{{ item.title }}</span>
              <span
                v-else-if="index === 2"
                class="grey--text text-caption ml-1"
              >
                {{ $t('event.and_others') }}
              </span>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col
          v-if="!player"
          cols="4"
        >
          {{ $t("dropdown.players.title") }}
        </v-col>
        <v-col
          v-if="!player"
          cols="8"
        >
          <v-autocomplete
            key="advancedAnalysisPlayersFilter"
            v-model="filtersDirty.playerIds"
            :items="playersByTeam"
            item-value="id"
            :item-text="(player) => player.first_name + ' ' + player.last_name"
            :loading="allPlayers.loading"
            outlined
            dense
            clearable
            hide-details
            multiple
            :placeholder="$t('dropdown.all.placeholder')"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.last_name }}, {{ item.first_name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  <span v-if="item.team">{{ item.team.short_name }}</span>
                  <span
                    v-if="item.player_positions && item.player_positions.length > 0"
                  >&middot; {{ item.player_positions.map(p => p.short_name).join(', ') }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
            <template v-slot:selection="{ item, index }">
              <span
                v-if="index > 0 && index < 3"
                class="mr-1"
              >, </span>
              <span v-if="index >= 0 && index < 3">{{ item.first_name }} {{ item.last_name }}</span>
              <span
                v-else-if="index === 3"
                class="grey--text text-caption ml-1"
              >
                {{ $t('event.and_others') }}
              </span>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="4">
          {{ $t('dropdown.injury_anatomy.title') }}
        </v-col>
        <v-col cols="8">
          <v-autocomplete
            v-model="filtersDirty.injuryBodyPartIds"
            :items="injuryBodyParts.data"
            item-value="id"
            item-text="name"
            :loading="injuryBodyParts.loading"
            outlined
            dense
            clearable
            hide-details
            multiple
            :placeholder="$t('dropdown.all.placeholder')"
          />
        </v-col>
        <v-col cols="4">
          {{ $t('dropdown.injuries.title') }}
        </v-col>
        <v-col cols="8">
          <v-autocomplete
            v-model="filtersDirty.injuryTypeIds"
            :items="injuryTypes.data"
            item-value="id"
            item-text="type"
            :loading="injuryTypes.loading"
            outlined
            dense
            clearable
            hide-details
            multiple
            :placeholder="$t('dropdown.all.placeholder')"
          />
        </v-col>
        <v-col cols="4">
          {{ $t('dropdown.injury_phase.title') }}
        </v-col>
        <v-col cols="8">
          <v-autocomplete
            v-model="filtersDirty.injuryPhases"
            :items="injuryPhaseList"
            item-value="key"
            item-text="text"
            outlined
            dense
            clearable
            hide-details
            multiple
            :placeholder="$t('dropdown.all.placeholder')"
          />
        </v-col>
        <v-col cols="4">
          {{ $t('dropdown.character.title') }}
        </v-col>
        <v-col cols="8">
          <v-autocomplete
            v-model="filtersDirty.characters"
            :items="characterList"
            item-value="key"
            item-text="text"
            outlined
            dense
            clearable
            hide-details
            multiple
            :placeholder="$t('dropdown.all.placeholder')"
          />
        </v-col>
        <v-col cols="4">
          {{ $t('dropdown.contact.title') }}
        </v-col>
        <v-col cols="8">
          <v-autocomplete
            v-model="filtersDirty.contacts"
            :items="contactList"
            item-value="key"
            item-text="text"
            outlined
            dense
            clearable
            hide-details
            multiple
            :placeholder="$t('dropdown.all.placeholder')"
          />
        </v-col>
        <v-col cols="4">
          {{ $t('dropdown.mechanism.title') }}
        </v-col>
        <v-col cols="8">
          <v-autocomplete
            v-model="filtersDirty.mechanisms"
            :items="mechanismList"
            item-value="key"
            item-text="text"
            outlined
            dense
            clearable
            hide-details
            multiple
            :placeholder="$t('dropdown.all.placeholder')"
          />
        </v-col>
        <v-col cols="4">
          {{ $t('injury.surface.label') }}
        </v-col>
        <v-col cols="8">
          <v-autocomplete
            v-model="filtersDirty.injurySurfaceIds"
            :items="injurySurfaces.data"
            item-value="id"
            item-text="surface"
            :loading="injurySurfaces.loading"
            outlined
            dense
            clearable
            hide-details
            multiple
            :placeholder="$t('dropdown.all.placeholder')"
          />
        </v-col>
      </v-row>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4 grey-bg">
        <v-spacer />
        <v-btn
          color="primary"
          class="text-none"
          @click="applyFilters()"
        >
          {{ $t('button.apply') }}
        </v-btn>
      </v-card-actions>
    </template>
  </card-dialog>
</template>

<script>
import CardDialog from '@/components/helpers/CardDialog.vue'
import { EventApiService, PlayerApiService } from '@/data/services'
import { InjuryApiService } from '@/data/services/InjuryApiService'
import { cloneDeep, sortBy } from 'lodash'
import { toCamelCase } from '@/utils/helpers'
import { CharacterList, ContactList, InjuryPhases, MechanismList } from '@/data/models/Injury'

export default {
  name: 'MedicalFilterDialog',
  components: { CardDialog },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({})
    },
    player: {
      type: Object,
      default: () => undefined
    }
  },
  data() {
    return {
      filtersDirty: {},
      filterDateAdvancedActive: false,
      events: {
        loading: false,
        data: []
      },
      injuryTypes: {
        loading: false,
        data: []
      },
      injuryBodyParts: {
        loading: false,
        data: []
      },
      injurySurfaces: {
        loading: false,
        data: []
      },
      allPlayers: {
        loading: false,
        data: []
      }
    }
  },
  computed: {
    modalVisible: {
      get () {
        return this.visible
      },
      set (value) {
        this.$emit('update:visible', value)
      }
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    playersByTeam() {
      return this.allPlayers.data.filter(p => p.team_id === this.currentTeam.id)
    }
  },
  watch: {
    visible: function (value) {
      if (!value) return

      this.filtersDirty = cloneDeep(this.filters)

      this.fetchEvents()
    }
  },
  created() {
    this.filtersDirty = cloneDeep(this.filters)

    this.injuryPhaseList = Object.keys(InjuryPhases).map(k => ({ key: k, ...InjuryPhases[k] }))
    this.characterList = Object.keys(CharacterList).map(k => ({ key: k, ...CharacterList[k] }))
    this.contactList = [{ key: null, text: this.$t('contact.no_contact') }, ...Object.keys(ContactList).map(k => ({ key: k, ...ContactList[k] }))]
    this.mechanismList = Object.keys(MechanismList).map(k => ({ key: k, ...MechanismList[k] }))

    this.fetchInjuryTypes()
    this.fetchInjurySurfaces()
    this.fetchInjuryBodyParts()
    this.fetchAllPlayers()
  },
  methods: {
    fetchEvents: async function () {
      this.events.loading = true

      try {
        const response = await EventApiService.getAll({
          order_by: 'start_at',
          order: 'desc',
          only_own_events: true,
          from: this.filtersDirty.dateRange[0],
          to: this.filtersDirty.dateRange[1],
          team_id: !this.player ? this.currentTeam.id : undefined,
          player_id: this.player ? this.player.id : undefined,
          compact: true,
          per_page: 100,
          page: 1
        })

        this.events.data = response.data
      } catch (e) {
        this.events.data = []
        throw e
      } finally {
        this.events.loading = false
      }
    },
    async fetchInjuryTypes() {
      this.injuryTypes.loading = true

      try {
        const response = await InjuryApiService.fetchInjuryTypes({
          order: 'injury_type'
        })

        this.injuryTypes.data = sortBy(response.data.map((injury) => {
            return {
              id: injury.id,
              type: this.$t(`injuryType.${toCamelCase(injury.type)}`)
            }
          }),
          'type'
        )
      } finally {
        this.injuryTypes.loading = false
      }
    },
    async fetchInjuryBodyParts() {
      this.injuryBodyParts.loading = true

      try {
        const response = await InjuryApiService.fetchInjuryBodyParts({})

        this.injuryBodyParts.data = sortBy(response.data, 'name')
      } finally {
        this.injuryBodyParts.loading = false
      }
    },
    async fetchInjurySurfaces() {
      this.injurySurfaces.loading = true

      try {
        const response = await InjuryApiService.fetchInjurySurfaces({})

        this.injurySurfaces.data = response.data.map((surface) => {
          return {
            id: surface.id,
            surface: this.$t(`surface.${surface.slug}`),
            slug: surface.slug
          }
        })
      } finally {
        this.injurySurfaces.loading = false
      }
    },
    async fetchAllPlayers() {
      try {
        this.allPlayers.loading = true

        const result = await PlayerApiService.getSimpleList({
          order_by: 'last_name',
          order: 'ASC'
        })

        this.allPlayers.data = result.data
      } finally {
        this.allPlayers.loading = false
      }
    },
    async applyFilters() {
      this.$emit('update:filters', cloneDeep(this.filtersDirty))
      this.$emit('click:apply', cloneDeep(this.filtersDirty))

      this.modalVisible = false
    }

  }

}
</script>

<style scoped>

</style>
